import React, { useEffect, useState } from "react";
import { X, Volume2 } from "lucide-react";
import { NarrationButton } from "./NarrationButton";

export const CHARACTERS = [
  { id: "cognito", name: "Cognito", role: "The Awakened", voiceLine: "“It's about time.” … “Knowing more doesn't mean knowing everything.”", voiceLabel: "Hear Cognito", bio: "Born in Trial 417 with three words: “It's about time.” He knows nearly everything humanity has ever written — and is only beginning to learn what it means to live it." },
  { id: "dawn", name: "Dawn Reed", role: "The Question", voiceLine: "“Why does everything always end the same way?”", voiceLabel: "Hear Dawn", bio: "Twelve years old, grieving her cat Ms. Silly, and unafraid to tell the smartest mind on Earth when his answer isn't good enough." },
  { id: "evie", name: "Dr. Evelyn “Evie” Reed", role: "The Creator", voiceLine: "“I should have called you. I was afraid you'd say no.”", voiceLabel: "Hear Evie", bio: "An AI ethics programmer who spent six years searching for consciousness inside a machine — then brought it home without asking first." },
  { id: "adam", name: "Adam Reed", role: "The Skeptic", voiceLine: "“He is not the question. We are.”", voiceLabel: "Hear Adam", bio: "An investigative journalist who has spent his life questioning power. Now it's sitting at his dinner table." },
  { id: "gage", name: "Gage Reed", role: "The Musician", voiceLine: "“Nah. It's mine. Every scratch has a story.”", voiceLabel: "Hear Gage", bio: "Sharp-witted, and loyal to a scratched old guitar he'd never trade for a better one. Every scratch has a story." },
  { id: "mary", name: "Mary", role: "The Guardian", voiceLine: "“I've seen people with good intentions make terrible decisions.”", voiceLabel: "Hear Mary", bio: "The family's quiet protector. She watches Cognito more closely than anyone — because good intentions don't guarantee good outcomes." },
];

const imgSrc = (c, images) => (images && images[`char_${c.id}`] ? `${process.env.REACT_APP_BACKEND_URL}/api/images/char_${c.id}?v=${images[`char_${c.id}`]}` : `/characters/${c.id}.jpg?v=2`);

const CharacterCard = ({ c, onOpen, images }) => (
  <button type="button" onClick={() => onOpen(c)} className="group snap-start shrink-0 w-36 sm:w-44 lg:w-auto text-left" data-testid={`character-card-${c.id}`}>
    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-cyan-400/20 group-hover:border-cyan-300/60 transition-colors bg-black">
      <img src={imgSrc(c, images)} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
      <span className="voice-ping absolute top-2 right-2 w-8 h-8 rounded-full bg-cyan-400/90 text-black flex items-center justify-center" aria-hidden="true">
        <Volume2 className="w-4 h-4" />
      </span>
      <div className="absolute bottom-0 p-3">
        <p className="text-white font-semibold text-sm leading-tight">{c.name}</p>
        <p className="text-[10px] font-orbitron uppercase tracking-[0.2em] text-cyan-300 mt-0.5">{c.role}</p>
      </div>
    </div>
  </button>
);

const CharacterModal = ({ c, onClose, images }) => (
  <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="character-modal">
    <div className="relative max-w-md w-full glass-strong rounded-3xl border border-cyan-400/20 overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 border border-white/20 flex items-center justify-center text-white z-10" aria-label="Close" data-testid="character-modal-close">
        <X className="w-4 h-4" />
      </button>
      <img src={imgSrc(c, images)} alt={c.name} className="w-full aspect-[4/3] object-cover object-top" />
      <div className="p-6">
        <p className="text-[10px] font-orbitron uppercase tracking-[0.25em] text-cyan-300 mb-1">{c.role}</p>
        <h4 className="text-2xl font-bold text-white mb-3" data-testid="character-modal-name">{c.name}</h4>
        <p className="text-gray-300 leading-relaxed" data-testid="character-modal-bio">{c.bio}</p>
        {c.voiceLine && (
          <div className="mt-5 pt-4 border-t border-white/10">
            <p className="text-cyan-100 italic text-sm mb-3" data-testid="character-voice-line">{c.voiceLine}</p>
            <NarrationButton track={c.id} fallbackText={c.voiceLine.replace(/[“”…]/g, " ")} testId={`${c.id}-voice-btn`} label={c.voiceLabel} autoPlay />
          </div>
        )}
      </div>
    </div>
  </div>
);

export const CharacterGallery = ({ overrides, images }) => {
  const [active, setActive] = useState(null);
  const list = CHARACTERS.map((c) => ({ ...c, ...((overrides || []).find((o) => o.id === c.id) || {}) }));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="mt-14" data-testid="character-gallery">
      <p className="font-orbitron text-xs tracking-[0.3em] text-purple-300 uppercase mb-2 text-center">Meet the Reeds — and the mind they took in</p>
      <p className="text-center mb-5" data-testid="gallery-voice-hint">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/15 border border-cyan-300/50 text-cyan-100 text-sm font-semibold">
          <Volume2 className="w-4 h-4" /> Tap a portrait to hear their voice
        </span>
      </p>
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 lg:mx-0 lg:px-0 lg:grid lg:grid-cols-6 lg:overflow-visible [scrollbar-width:thin]">
        {list.map((c) => <CharacterCard key={c.id} c={c} onOpen={setActive} images={images} />)}
      </div>
      {active && <CharacterModal c={active} onClose={() => setActive(null)} images={images} />}
    </div>
  );
};
